import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  initializeOneSignal,
  isOneSignalInitialized,
  requestPushPermission,
  setExternalUserId,
  removeExternalUserId,
  sendTag,
  sendTags,
  isPushEnabled,
} from '@/services/onesignal';
import { isNativeApp } from '@/utils/platform';
import { isDespiaNative, requestNativeNotificationPermission, openNativeSettings } from '@/utils/despia';

const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID;

// Motivational quotes for push notifications
const MOTIVATIONAL_QUOTES = [
  { quote: "The only bad workout is the one that didn't happen", cta: "Let's go!" },
  { quote: "Pain is temporary, glory is forever", cta: "Time to evolve." },
  { quote: "You don't find willpower, you create it", cta: "Create it now." },
  { quote: "Champions are made when no one is watching", cta: "Be a champion." },
  { quote: "Your future self will thank you", cta: "Start now." },
  { quote: "Discipline is choosing what you want most over what you want now", cta: "Choose greatness." },
  { quote: "The body achieves what the mind believes", cta: "Believe today." },
  { quote: "Every rep counts. Every day matters", cta: "Make it count." },
  { quote: "Strong is not given. It's earned", cta: "Earn it." },
  { quote: "Comfort zone? That's where dreams go to die", cta: "Break free." },
  { quote: "You're one workout away from a better mood", cta: "Let's move." },
  { quote: "Legends don't skip days", cta: "Be legendary." },
];

export function useOneSignalNotifications() {
  const { user } = useAuthContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize OneSignal on mount
  useEffect(() => {
    const init = async () => {
      if (!ONESIGNAL_APP_ID) {
        console.warn('[useOneSignalNotifications] No App ID configured');
        setIsLoading(false);
        return;
      }

      const success = await initializeOneSignal(ONESIGNAL_APP_ID);
      setIsInitialized(success);

      if (success) {
        const enabled = await isPushEnabled();
        setIsEnabled(enabled);
      }

      setIsLoading(false);
    };

    init();
  }, []);

  // NOTE: External user ID is now set in AuthContext.tsx using the user's email
  // This ensures OneSignal can target users by email address for push notifications

  // Request push notification permission
  // forceAttempt: bypasses the denied check to allow re-prompting after user resets permissions
  const requestPermission = useCallback(async (forceAttempt = false): Promise<boolean> => {
    console.log('[useOneSignalNotifications] Requesting permission, forceAttempt:', forceAttempt);
    
    // Check Despia FIRST - this is the actual native environment used by the app
    if (isDespiaNative()) {
      console.log('[useOneSignalNotifications] Despia native detected, using native permission flow');
      setIsLoading(true);
      
      try {
        const granted = await requestNativeNotificationPermission();
        console.log('[useOneSignalNotifications] Native permission result:', granted);
        setIsEnabled(granted);
        setIsLoading(false);
        return granted;
      } catch (error) {
        console.error('[useOneSignalNotifications] Native permission error:', error);
        setIsLoading(false);
        return false;
      }
    }
    
    // Check Capacitor-based native (fallback for future use)
    if (isNativeApp()) {
      console.log('[useOneSignalNotifications] Capacitor native detected, opening settings');
      openNativeSettings();
      return false;
    }
    
    // WEB PATH - use browser Notification API
    if (!('Notification' in window)) {
      console.warn('[useOneSignalNotifications] Notifications not supported in this browser');
      return false; // Don't fake success!
    }
    
    // If already granted, just return true
    if (Notification.permission === 'granted') {
      console.log('[useOneSignalNotifications] Permission already granted');
      setIsEnabled(true);
      return true;
    }
    
    // If denied AND not forcing, we can't request again
    if (Notification.permission === 'denied' && !forceAttempt) {
      console.log('[useOneSignalNotifications] Permission previously denied, not forcing');
      return false;
    }
    
    if (forceAttempt && Notification.permission === 'denied') {
      console.log('[useOneSignalNotifications] Force attempt - trying to prompt despite denied state');
    }
    
    try {
      setIsLoading(true);
      
      if (!isInitialized) {
        // Fall back to browser notifications if OneSignal not initialized
        console.log('[useOneSignalNotifications] Using browser Notification API');
        const permission = await Notification.requestPermission();
        const granted = permission === 'granted';
        setIsEnabled(granted);
        setIsLoading(false);
        
        if (granted) {
          // Show welcome notification
          const { quote } = MOTIVATIONAL_QUOTES[Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length)];
          try {
            new Notification('🔥 Evolve Notifications Active', {
              body: `"${quote}" — You're 4.7× more likely to hit Immortal now.`,
              icon: '/favicon.ico',
            });
          } catch (e) {
            console.log('[useOneSignalNotifications] Could not show welcome notification:', e);
          }
        }
        
        return granted;
      }

      const granted = await requestPushPermission();
      setIsEnabled(granted);
      setIsLoading(false);
      return granted;
    } catch (error) {
      console.error('[useOneSignalNotifications] Permission request failed:', error);
      setIsEnabled(false);
      setIsLoading(false);
      return false; // Don't fake success on error!
    }
  }, [isInitialized]);

  // Update user tags for segmentation
  const updateUserTags = useCallback(async (tags: {
    tier?: string;
    goal?: string;
    experience?: string;
    workoutDays?: string;
  }) => {
    if (!isInitialized) return;

    const tagsToSend: Record<string, string> = {};
    
    if (tags.tier) tagsToSend['tier'] = tags.tier;
    if (tags.goal) tagsToSend['goal'] = tags.goal;
    if (tags.experience) tagsToSend['experience'] = tags.experience;
    if (tags.workoutDays) tagsToSend['workout_days'] = tags.workoutDays;

    await sendTags(tagsToSend);
  }, [isInitialized]);

  // Send a single tag
  const setTag = useCallback(async (key: string, value: string) => {
    if (!isInitialized) return;
    await sendTag(key, value);
  }, [isInitialized]);

  // Get a random motivational quote
  const getRandomQuote = useCallback(() => {
    return MOTIVATIONAL_QUOTES[Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length)];
  }, []);

  // Check if we should prompt for permissions
  const shouldPromptForPermission = useCallback(() => {
    if (isEnabled) return false;
    if (!isInitialized && !('Notification' in window)) return false;
    if ('Notification' in window && Notification.permission === 'denied') return false;
    return true;
  }, [isEnabled, isInitialized]);

  return {
    isInitialized,
    isEnabled,
    isLoading,
    isSupported: isInitialized || 'Notification' in window,
    requestPermission,
    updateUserTags,
    setTag,
    getRandomQuote,
    shouldPromptForPermission,
  };
}
